import { type LoadedDocumentSnapshot, type SnapshotMeta } from '@/document/model'
import type { EventTuple } from '@/events/types'
import { Document } from '@/events/document'
import type { AppEventLog } from '@/events/appLog'
import { encodeCheckpoint, decodeCheckpoint, type CheckpointBlob } from '@/persistence/checkpointCodec'
import { extractTailSinceCommitId, latestCheckpointSummary } from '@/persistence/core'
import {
  type DocumentMeta,
  type SavedDocument,
  readDocument,
  writeDocumentRecord,
  listDocuments,
  deleteDocument as deleteDocumentRecord,
  discardDocument,
  getDocumentThumbnail,
  setDocumentThumbnail,
  getDocumentThumbnailMeta,
  setDocumentThumbnailMeta,
  isThumbnailStale,
  subscribeDocumentsIndex,
  upsertIndex,
} from '@/persistence/localStore'
import type { VoxelMap } from '@/voxelTypes'
import { computeBoundsFromWindow } from '@/core/bounds'
import { computeCameraSnapshotForBounds, DEFAULT_FIT_OPTS } from '@/core/cameraFit'
import { computeBoundsFromSurface } from '@/core/surface'
import { hydrateMeta, initialMeta, type HydrationMode } from '@/document/hydration'
import { createLogger } from '@/debug/logger'
import { createDocumentBase } from '@/storage/grid/documentBase'
import { reduceEvents } from '@/events/reducer'

// Lightweight localStorage-based persistence for autosave and document browsing.
// Records store a materialized checkpoint plus replayable tail events.

// Re-export types for external consumers
export type { DocumentMeta, SavedDocument } from '@/persistence/localStore'

const log = createLogger('persistence')
type CameraMode = 'orbit' | 'tilt'

function isCameraMode(value: unknown): value is CameraMode {
  return value === 'orbit' || value === 'tilt'
}

function preferredCameraMode(meta: Partial<SnapshotMeta>): CameraMode {
  if (isCameraMode(meta.prefersCameraMode)) return meta.prefersCameraMode
  if (isCameraMode(meta.camera?.mode)) return meta.camera.mode
  return isCameraMode(DEFAULT_FIT_OPTS.cameraMode) ? DEFAULT_FIT_OPTS.cameraMode : 'orbit'
}

function computeInitialCameraForBounds(bounds: SnapshotMeta['bounds'], cameraMode: CameraMode) {
  if (cameraMode !== 'tilt') {
    return computeCameraSnapshotForBounds(bounds, { ...DEFAULT_FIT_OPTS, cameraMode })
  }

  return computeCameraSnapshotForBounds(bounds, {
    ...DEFAULT_FIT_OPTS,
    cameraMode,
    targetYOffset: 0,
    margin: 1.18,
    zoom: 0.8,
  })
}

function generateDocumentId(): string {
  if (typeof crypto !== 'undefined') {
    const scoped = crypto as { randomUUID?: () => string }
    if (typeof scoped?.randomUUID === 'function') {
      return scoped.randomUUID()
    }
  }
  return `document-${Date.now()}`
}

let lastSaveRevision = 0

function nextSaveRevision(afterRevision: number = 0): number {
  const now = Date.now() * 1000
  lastSaveRevision = Math.max(now, lastSaveRevision + 1, afterRevision + 1)
  return lastSaveRevision
}

type PersistSnapshotOptions = {
  id?: string
  base: VoxelMap
  meta: SnapshotMeta
  events?: EventTuple[]
  name?: string
  commitId?: number
  checkpointCommitId?: number
  hydrationMode: HydrationMode
  context: string
}

function normalizedDocumentName(value: string | null | undefined, fallback: string): string {
  const trimmed = value?.trim()
  return trimmed ? trimmed : fallback
}

function savedDocumentMeta(
  id: string,
  prior: SavedDocument | null,
  doc: Document,
  now: string,
  commitId: number,
): DocumentMeta {
  return {
    id,
    name: normalizedDocumentName(prior?.name ?? doc.name, 'Untitled'),
    createdAt: prior?.createdAt ?? doc.createdAt?.toISOString?.() ?? now,
    updatedAt: now,
    commitId,
  }
}

async function persistSnapshotRecord(opts: PersistSnapshotOptions): Promise<string> {
  const now = new Date().toISOString()
  const id = opts.id ?? generateDocumentId()
  const prior = readDocument(id)
  const saveRevision = nextSaveRevision(prior?.saveRevision)
  const events = Array.isArray(opts.events) ? [...opts.events] : []
  const hydratedMeta = hydrateMeta(opts.meta, opts.hydrationMode)

  let checkpoint: CheckpointBlob
  try {
    checkpoint = await encodeCheckpoint(opts.base, hydratedMeta, 32)
  } catch (err) {
    log.error(`[${opts.context}] checkpoint encode failed`, { id, err })
    throw err
  }

  const name = normalizedDocumentName(opts.name ?? prior?.name, 'Untitled')
  const createdAt = prior?.createdAt ?? now
  const commitId =
    typeof opts.commitId === 'number' && opts.commitId >= 0 ? Math.floor(opts.commitId) : events.length
  const checkpointCommitId =
    typeof opts.checkpointCommitId === 'number' && opts.checkpointCommitId >= 0
      ? Math.floor(opts.checkpointCommitId)
      : Math.max(0, commitId - events.length)

  const saved: SavedDocument = {
    id,
    name,
    createdAt,
    updatedAt: now,
    events,
    checkpoint,
    checkpointCommitId,
    commitId,
    saveRevision,
  }

  if (writeDocumentRecord(id, saved)) {
    upsertIndex({ id, name, createdAt, updatedAt: now, commitId })
  }
  return id
}

// --- Autosave ---------------------------------------------
// Quicksave is immediate (PersistenceGate debounces log pointer changes itself).

export async function saveAutosaveFromLog(appLog: AppEventLog, doc: Document, id: string): Promise<void> {
  if (appLog.isDraftActive()) return

  const now = new Date().toISOString()
  const commitId = appLog.getDurableCommitId()
  const prior = readDocument(id)
  log.debug('saveAutosaveFromLog', { id, commitId, priorCommitId: prior?.commitId })
  if (prior?.commitId === commitId) {
    log.debug('skip autosave (commitId unchanged)', { id, commitId })
    return
  }
  const saveRevision = nextSaveRevision(prior?.saveRevision)

  if (prior && typeof prior.commitId === 'number' && commitId < prior.commitId) {
    log.debug('autosave storing earlier history pointer', { id, priorCommitId: prior.commitId, commitId })
  }

  const checkpointSummary = latestCheckpointSummary(appLog)
  if (!checkpointSummary && !prior?.checkpoint) {
    throw new Error(`saveAutosaveFromLog: no checkpoint available for ${id}`)
  }

  let checkpointBlob: CheckpointBlob | null = prior?.checkpoint ?? null
  let checkpointCommitId = prior?.checkpointCommitId ?? 0
  const shouldEncodeNewCheckpoint =
    !!checkpointSummary && (!prior || prior.checkpointCommitId !== checkpointSummary.commitId)

  if (shouldEncodeNewCheckpoint && checkpointSummary) {
    try {
      const checkpointMeta = checkpointSummary.meta
      if (!checkpointMeta) throw new Error(`saveAutosaveFromLog: checkpoint meta missing for ${id}`)
      checkpointBlob = await encodeCheckpoint(checkpointSummary.base, checkpointMeta, 32)
      checkpointCommitId = checkpointSummary.commitId
    } catch (err) {
      log.error('checkpoint encode failed', { id, err })
      if (!prior?.checkpoint) throw err
      checkpointBlob = prior.checkpoint
      checkpointCommitId = prior.checkpointCommitId
    }
  }

  if (!checkpointBlob) {
    throw new Error(`saveAutosaveFromLog: missing checkpoint after evaluation for ${id}`)
  }

  const tail = extractTailSinceCommitId(appLog, checkpointCommitId)
  const metaBase = savedDocumentMeta(id, prior, doc, now, commitId)
  const record: SavedDocument = { ...metaBase, events: tail, checkpoint: checkpointBlob, checkpointCommitId, saveRevision }
  if (writeDocumentRecord(id, record)) {
    upsertIndex(metaBase)
  }
}

// Synchronous emergency save for window unload: only persist pending tail events, skip async checkpoint encoding
export function persistTailSync(appLog: AppEventLog, doc: Document, id: string): void {
  if (appLog.isDraftActive()) return

  const commitId = appLog.getDurableCommitId()
  const prior = readDocument(id)

  if (!prior) {
    log.warn('persistTailSync skipped (no prior document)', { id })
    return
  }
  if (prior?.commitId === commitId) {
    log.debug('saveEventsSync skipped (commitId unchanged)', { id, commitId })
    return
  }
  if (appLog.getPointer() < 0) {
    const cp = latestCheckpointSummary(appLog)
    if (!cp || prior.checkpointCommitId !== cp.commitId) {
      log.debug('persistTailSync skipped (current checkpoint requires async save)', {
        id,
        priorCheckpointCommitId: prior.checkpointCommitId,
        checkpointCommitId: cp?.commitId,
      })
      return
    }
  }
  const saveRevision = nextSaveRevision(prior.saveRevision)

  const now = new Date().toISOString()
  const tail = extractTailSinceCommitId(appLog, prior.checkpointCommitId)

  const metaBase = savedDocumentMeta(id, prior, doc, now, commitId)
  const payload: SavedDocument = {
    ...metaBase,
    events: tail,
    checkpoint: prior.checkpoint,
    checkpointCommitId: prior.checkpointCommitId,
    saveRevision,
  }

  if (writeDocumentRecord(id, payload)) {
    upsertIndex(metaBase)
  }
}

// Force an immediate checkpoint encode + persist for the current pointer.
// Useful when the caller needs storage to reflect the exact in-memory checkpoint
// (e.g., before kicking asynchronous consumers like thumbnail jobs).
export async function flushCheckpointNow(appLog: AppEventLog, doc: Document, id: string): Promise<void> {
  if (appLog.isDraftActive()) return

  const now = new Date().toISOString()
  const commitId = appLog.getDurableCommitId()
  const prior = readDocument(id)
  const saveRevision = nextSaveRevision(prior?.saveRevision)
  const cp = latestCheckpointSummary(appLog)

  // Base metadata for the documents index.
  const metaBase = savedDocumentMeta(id, prior, doc, now, commitId)

  if (!cp) {
    if (!prior) throw new Error(`flushCheckpointNow: no checkpoint available for ${id}`)
    const tail = extractTailSinceCommitId(appLog, prior.checkpointCommitId)
    if (writeDocumentRecord(id, {
      ...metaBase,
      events: tail,
      checkpoint: prior.checkpoint,
      checkpointCommitId: prior.checkpointCommitId,
      saveRevision,
    })) {
      upsertIndex(metaBase)
    }
    return
  }

  try {
    const checkpointMeta = cp.meta
    if (!checkpointMeta) throw new Error(`flushCheckpointNow: checkpoint meta missing for ${id}`)
    const blob = await encodeCheckpoint(cp.base, checkpointMeta, 32)
    const tail = extractTailSinceCommitId(appLog, cp.commitId)
    if (writeDocumentRecord(id, {
      ...metaBase,
      events: tail,
      checkpoint: blob as CheckpointBlob,
      checkpointCommitId: cp.commitId,
      saveRevision,
    })) {
      upsertIndex(metaBase)
    }
  } catch (err) {
    log.error('checkpoint encode failed', { id, err })
    if (!prior) throw err
    const tail = extractTailSinceCommitId(appLog, prior.checkpointCommitId)
    if (writeDocumentRecord(id, {
      ...metaBase,
      events: tail,
      checkpoint: prior.checkpoint,
      checkpointCommitId: prior.checkpointCommitId,
      saveRevision,
    })) {
      upsertIndex(metaBase)
    }
  }
}

/**
 * Create a brand new document with a provided meta snapshot.
 * - Writes a checkpoint (empty base + meta) so the loader can hydrate directly.
 * - Updates the documents index.
 * Returns the new document id.
 */
export async function createDocumentWithMeta(
  metaOverrides: Partial<SnapshotMeta>,
  id?: string,
  opts?: { name?: string }
): Promise<string> {
  const baseMeta = initialMeta()
  let bounds
  if (metaOverrides.bounds) bounds = metaOverrides.bounds
  else if (metaOverrides.surface && typeof metaOverrides.surface.kind === 'string') {
    // Compute bounds directly from any supported surface kind
    bounds = computeBoundsFromSurface(metaOverrides.surface)
  } else {
    bounds = computeBoundsFromWindow()
  }
  const camera = metaOverrides.camera ?? computeInitialCameraForBounds(bounds, preferredCameraMode(metaOverrides))
  const meta = { ...baseMeta, ...metaOverrides, bounds, camera }

  const base: VoxelMap = createDocumentBase(undefined, undefined, bounds)
  return persistSnapshotRecord({
    id,
    base,
    meta: meta as SnapshotMeta,
    events: [],
    name: opts?.name,
    commitId: 0,
    checkpointCommitId: 0,
    hydrationMode: 'strict',
    context: 'createDocumentWithMeta',
  })
}

export async function createDocumentFromSnapshot(opts: {
  base: VoxelMap
  meta: SnapshotMeta
  events?: EventTuple[]
  name?: string
  id?: string
  commitId?: number
  checkpointCommitId?: number
}): Promise<string> {
  const events = opts.events ?? []
  const name = opts.name && opts.name.trim() ? opts.name.trim() : 'Imported Document'
  const commitId =
    typeof opts.commitId === 'number' && opts.commitId >= 0 ? Math.floor(opts.commitId) : events.length
  const checkpointCommitId =
    typeof opts.checkpointCommitId === 'number' && opts.checkpointCommitId >= 0
      ? Math.floor(opts.checkpointCommitId)
      : Math.max(0, commitId - events.length)

  return persistSnapshotRecord({
    id: opts.id,
    base: opts.base,
    meta: opts.meta as SnapshotMeta,
    events,
    name,
    commitId,
    checkpointCommitId,
    hydrationMode: 'lenient',
    context: 'createDocumentFromSnapshot',
  })
}

// Preferred loader for seedless consumers: returns the fully materialized document state.
export async function loadDocumentSnapshot(id: string): Promise<LoadedDocumentSnapshot> {
  const saved = readDocument(id)
  if (!saved) throw new Error(`Document ${id} not found in local storage`)

  const tailEvents = saved.events || []
  const commitId = typeof saved.commitId === 'number' && saved.commitId >= 0 ? saved.commitId : tailEvents.length

  const { base, meta } = await decodeCheckpoint(saved.checkpoint)
  const hydratedMeta = hydrateMeta(meta, 'strict')
  const shouldRefitEmptyTiltDocument = base.size === 0 && tailEvents.length === 0 && hydratedMeta.prefersCameraMode === 'tilt'
  const checkpointState = {
    base,
    ui: {},
    meta: shouldRefitEmptyTiltDocument
      ? { ...hydratedMeta, camera: computeInitialCameraForBounds(hydratedMeta.bounds, 'tilt') }
      : hydratedMeta,
  }
  const materialized = reduceEvents(checkpointState, tailEvents)
  return {
    base: materialized.base,
    meta: hydrateMeta(materialized.meta, 'strict'),
    commitId,
    checkpointCommitId: commitId,
  }
}

export { deleteDocumentRecord, discardDocument, listDocuments, readDocument, getDocumentThumbnail, setDocumentThumbnail, getDocumentThumbnailMeta, setDocumentThumbnailMeta, isThumbnailStale, subscribeDocumentsIndex }
